import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

// Linear's inline banner: 8px radius, 10/12px padding, 13px text, a 16px
// leading icon in the tone colour, and no shadow. Tones tint the fill, except
// warning, which keeps the neutral surface and carries its gold in the icon.
const alertVariants = cva(["relative w-full gap-3 rounded-control border px-3 py-2.5 text-base leading-5 text-text-primary", "[&>svg]:absolute [&>svg]:left-3 [&>svg]:top-3 [&>svg]:size-4 [&>svg~*]:pl-6"], {
  variants: {
    tone: {
      neutral: "border-border-primary bg-bg-primary [&>svg]:text-text-tertiary",
      info: "border-transparent bg-blue-bg-subtle [&>svg]:text-blue-text",
      success: "border-transparent bg-green-bg-subtle [&>svg]:text-green-text",
      warning: "border-border-primary bg-bg-primary [&>svg]:text-yellow-strong",
      danger: "border-transparent bg-red-bg-subtle [&>svg]:text-red-text"
    }
  },
  defaultVariants: {
    tone: "neutral"
  }
});
export type AlertProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants> & {
  ref?: React.Ref<HTMLDivElement>;
};

/** Use when inline content needs a status message with a tone icon. */
const Alert = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      tone,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = tone;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    tone = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== tone) {
    t1 = cn(alertVariants({
      tone
    }), className);
    $[5] = className;
    $[6] = tone;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <div ref={ref} role="alert" className={t1} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
Alert.displayName = "Alert";
const AlertTitle = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("mb-0.5 text-base font-medium leading-5", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <h5 ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
AlertTitle.displayName = "AlertTitle";

// `inline` runs the content beside the title on wide screens (notice stacks);
// `stack` spaces blocks of copy and actions vertically.
const alertContentVariants = cva("text-base leading-5 text-text-secondary", {
  variants: {
    layout: {
      block: "",
      inline: "flex flex-wrap items-baseline gap-x-3 gap-y-1",
      stack: "flex flex-col gap-2"
    }
  },
  defaultVariants: {
    layout: "block"
  }
});
const AlertContent = t0 => {
  const $ = _c(12);
  let className;
  let layout;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      layout,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = layout;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    layout = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== layout) {
    t1 = cn(alertContentVariants({
      layout
    }), className);
    $[5] = className;
    $[6] = layout;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
AlertContent.displayName = "AlertContent";
const AlertHeader = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex flex-row justify-between", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
AlertHeader.displayName = "AlertHeader";
export { Alert, AlertContent, AlertHeader, AlertTitle };